import { create } from 'zustand';
import { SupportedCurrency } from '../types';
import {
  sanitizeCurrency,
  calculateFiatPrice,
  formatPrice as formatPriceUtil,
  CURRENCY_RATES,
  RECHARGE_OVERRIDES,
} from '../utils/currency';

export const LOCAL_STORAGE_KEY = 'fn_currency';

export interface CurrencyState {
  currency: SupportedCurrency;
  rates: Record<SupportedCurrency, number>;
  rechargeOverrides: Record<string, Record<SupportedCurrency, number>>;

  // Actions
  setCurrency: (currency: SupportedCurrency) => void;
  toggleCurrency: () => void;

  // Computational Helpers
  calculatePrice: (vbucks: number, offerId?: string) => number;
  formatPrice: (amount: number) => string;
  formatVbucksPrice: (vbucks: number, offerId?: string) => string;
}

/**
 * Safely reads and sanitizes currency from localStorage.
 * - Defaults to 'ARS' on empty/missing storage.
 * - Strictly sanitizes legacy 'USD' or invalid values to 'ARS' and overwrites localStorage.
 * - Protected against SecurityError in restricted environments.
 */
export function readAndSanitizeStoredCurrency(): SupportedCurrency {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!stored) {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, 'ARS');
        return 'ARS';
      }
      const sanitized = sanitizeCurrency(stored);
      if (stored !== sanitized) {
        // Immediately overwrite dirty / legacy 'USD' values with 'ARS'
        window.localStorage.setItem(LOCAL_STORAGE_KEY, sanitized);
      }
      return sanitized;
    }
  } catch {
    // Graceful fallback if storage access is restricted
  }
  return 'ARS';
}

/**
 * Persists sanitized currency to localStorage and dispatches window events.
 */
export function persistCurrency(currency: SupportedCurrency): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const sanitized = sanitizeCurrency(currency);
      window.localStorage.setItem(LOCAL_STORAGE_KEY, sanitized);
      window.dispatchEvent(
        new CustomEvent('currencyChanged', { detail: sanitized })
      );
    }
  } catch {
    // Graceful fallback
  }
}

export const useCurrencyStore = create<CurrencyState>()((set, get) => {
  // Cross-tab and window event listener setup (only in browser environment)
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === LOCAL_STORAGE_KEY && event.newValue) {
        const sanitized = sanitizeCurrency(event.newValue);
        if (get().currency !== sanitized) {
          set({ currency: sanitized });
        }
      }
    });

    window.addEventListener('currencyChanged', ((event: CustomEvent<string>) => {
      const sanitized = sanitizeCurrency(event.detail);
      if (get().currency !== sanitized) {
        set({ currency: sanitized });
      }
    }) as EventListener);
  }

  return {
    currency: readAndSanitizeStoredCurrency(),
    rates: CURRENCY_RATES,
    rechargeOverrides: RECHARGE_OVERRIDES,

    setCurrency: (newCurrency: SupportedCurrency) => {
      const sanitized = sanitizeCurrency(newCurrency);
      persistCurrency(sanitized);
      set({ currency: sanitized });
    },

    toggleCurrency: () => {
      const current = get().currency;
      const next: SupportedCurrency = current === 'ARS' ? 'MXN' : 'ARS';
      persistCurrency(next);
      set({ currency: next });
    },

    calculatePrice: (vbucks: number, offerId?: string) => {
      return calculateFiatPrice(vbucks, get().currency, offerId);
    },

    formatPrice: (amount: number) => {
      return formatPriceUtil(amount, get().currency);
    },

    formatVbucksPrice: (vbucks: number, offerId?: string) => {
      const curr = get().currency;
      const fiat = calculateFiatPrice(vbucks, curr, offerId);
      return formatPriceUtil(fiat, curr);
    },
  };
});

export default useCurrencyStore;
